/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import {
    DateFormats,
    DayOption,
    HeldOfficeProps,
    IClinician,
    IOfficeWindow,
    IOfficeWindowSchedule,
    OfficeMangerOfficeWindow,
    RecurrenceRule,
} from "intake_shared/src/types";
import { Dialog } from "../../../bits/Dialog/Dialog";
import moment from "../../../../../utils/app_moment";
import {
    defaultOffices,
    getOfficeManagers,
} from "../../../../../store/cache/location";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../utils/intakeEmailSignature";
import { AclContext } from "../../../../../contexts/AclContext";
import { useToasts } from "react-toast-notifications";
import store from "../../../../../store";
import { v4 as uuidv4 } from "uuid";
import {
    findAvailableTimeSlotsClinicianCanBook,
    getAvailableRequestStartDates,
    groupAvailableTimesIntoWindows,
    getNextXWeeksMoment,
    getNumberOfWeeks,
    getStringFromScheduleDate,
    getTimesUsedByOtherClincians,
    handleFormatDatePickerValue,
    hasConflictingAvailability,
    hasConflictingAvailabilityWithCliniciansRemoteSchedules,
    isValidISODate,
    isWithinOfficeWindow,
    sortStringsWithNumberEndings,
    suggestAvailableOfficesAndTimes,
} from "../../../../../utils/utils";
import { Select } from "../../../bits/Select/Select";
import { uniq } from "lodash";
import { Input } from "../../../bits/Input/Input";
import { TextArea } from "../../../bits/TextArea/TextArea";
import { DatePicker } from "../../../bits/DatePicker/DatePicker";
import Alert from "../../../bits/Alert/Alert";
import styles from "../RequestOfficeWindow.module.scss";
interface TimeSlotProps {
    officeNumber: string;
    timeSlot: string;
    onClick: () => void;
}

const TimeSlot: React.FC<TimeSlotProps> = ({ officeNumber, timeSlot, onClick }) => {
    return (
        <span
            key={`${officeNumber}-${timeSlot}`}
            onClick={onClick}
            className={styles.timeSlot}
        >
            {`${timeSlot}`}
        </span>
    );
};

const generateSuggestedWindows = (
    suggestedOfficesAndTimes: Record<string, string[]>,
    handleClickTimeSlot: (
        officeNumber: string,
        selectedFromTime: string,
        selectedToTime: string
    ) => void
): JSX.Element[] => {
    const formattedWindows: JSX.Element[] = [];

    for (const officeNumber in suggestedOfficesAndTimes) {
        const times = suggestedOfficesAndTimes[officeNumber];

        if (times.length > 0) {
            const timeSlots = groupAvailableTimesIntoWindows(times).map(
                ({ fromTime, toTime }) => (
                    <TimeSlot
                        key={`${officeNumber}-${fromTime}-${toTime}`}
                        officeNumber={officeNumber}
                        timeSlot={`${fromTime} - ${toTime}`}
                        onClick={() => {
                            handleClickTimeSlot(officeNumber, fromTime, toTime);
                        }}
                    />
                )
            );

            if (timeSlots.length > 0) {
                // Check if there are any timeslots for this office
                formattedWindows.push(
                    <span
                        key={`result-${officeNumber}`}
                        className={styles.timeSlotsContainer}
                    >
                        <strong>Office({officeNumber}) </strong> -{" "}
                        <span className={styles.timeWindow}>{timeSlots}</span>
                    </span>
                );
            }
        }
    }

    return formattedWindows;
};

interface RequestDialogProps {
    show: boolean;
    onHide?: () => void;
}

interface SelectorDefault {
    day?: any;
    fromTime?: string;
    toTime?: string;
    startingDate?: moment.Moment;
    endingDate: moment.Moment | null;
    ending_date?: Date | string;
}

const defaultDate: SelectorDefault = {
    endingDate: null,
    ending_date: "",
};

const MAX_APPT_DATE_DIFF_WEEKS = 4;

type Option = {
    label: string;
    value: string;
};

interface RequestOfficeFormProps {
    requestOfficeDetails: RequestDialogProps;
    refreshKey: number;
    setRequestOfficeDetails: (
        value: React.SetStateAction<RequestDialogProps>
    ) => void;
    loggedInClinicianData?: IClinician;
    clinicianProfileLoading?: boolean;
    viewAs: "office-manager" | "clinician";
    setRefreshKey: (value: React.SetStateAction<number>) => void;
}

export default function RequestOfficeForm({
    requestOfficeDetails,
    refreshKey,
    setRequestOfficeDetails,
    loggedInClinicianData,
    clinicianProfileLoading = false,
    viewAs,
    setRefreshKey,
}: RequestOfficeFormProps) {
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const [clinicianList, setClinicianList] = React.useState<IClinician[]>([]);
    const { addToast } = useToasts();
    const [officeWindows, setOfficeWindows] = React.useState<
        OfficeMangerOfficeWindow[]
    >({} as OfficeMangerOfficeWindow[]);
    const [userSelectedOffice, setUserSelectedOffice] = React.useState(false);
    const [userSelectedFromTime, setUserSelectedFromTime] = React.useState(false);
    const [officeManagerLocations, setOfficeManagerLocations] =
        React.useState<string[]>();
    const [selectedEndTime, setSelectedEndTime] = React.useState(false);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [hasConflict, setHasConflict] = React.useState<IOfficeWindowSchedule>();
    const [newOfficeWindowClash, setNewOfficeWindowClash] =
        React.useState<IOfficeWindowSchedule>();
    const [
        requestHasConflictWithCliniciansSchedule,
        setRequestHasConflictWithCliniciansSchedule,
    ] = React.useState<IOfficeWindowSchedule>();
    const [selectedEndDate, setSelectedEndDate] = React.useState(false);
    const [isValidOfficeWindow, setIsValidOfficeWindow] = React.useState<boolean>();
    const [draftRequestOfficeWindow, setDraftRequestOfficeWindow] = React.useState({
        location: "",
        office: "",
        recurrence: RecurrenceRule.WEEKLY,
        start_date: new Date(),
        end_date: new Date(),
        ending_date: defaultDate.ending_date,
        change_end_time_reason: "",
        comment: "",
        request_date: new Date(),
        day: "",
        requester: loggedInUserEmail as string,
        assigned_to: "",
        clinician_name: "",
        category: "",
        waitlist_count: 0,
        request_list_count: 0,
        office_window_request_id: "",
        delivery_option: "",
    });

    const getOfficeManagerLocations = async () => {
        const getOfficeManager = await getOfficeManagers();
        const officeManagerLocationsofLoggedInUser = Object.keys(
            getOfficeManager || {}
        )?.filter((location) => {
            return (
                getOfficeManager &&
                getOfficeManager[location]?.includes(loggedInUserEmail as string)
            );
        });
        return officeManagerLocationsofLoggedInUser;
    };

    const [draftCreateOfficeWindow, setDraftCreateOfficeWindow] = React.useState({
        location: "",
        office: "",
        start: "",
        end: "",
        id: "",
    });
    const [selectedTimeFromSuggestion, setSelectedTimeFromSuggestion] =
        React.useState(false);
    const [selectedDay, setSelectedDay] = React.useState(defaultDate.day || "");
    const [selectedFromTime, setSelectedFromTime] = React.useState(
        defaultDate.fromTime || "07:00"
    );
    const [selectedToTime, setSelectedToTime] = React.useState(
        defaultDate.toTime || "08:00"
    );
    const [startingDate, setStartingDate] = React.useState(
        defaultDate.startingDate || moment()
    );
    const [selectedStartDate, setSelectedStartDate] = React.useState(false);
    const [endingDate, setEndingDate] = React.useState<
        moment.Moment | null | undefined
    >(defaultDate.endingDate);
    const [availableApptStartingDates, setAvailableApptStartingDates] =
        React.useState<moment.Moment[]>([]);
    const [formattedWindows, setFormattedWindows] = React.useState<JSX.Element[]>();
    const [heldWindows, setHeldWindows] = React.useState([] as HeldOfficeProps[]);
    const [availabilityDataLoading, setAvailabilityDataLoading] =
        React.useState(false);
    const [availabilityDataLoadedFor, setAvailabilityDataLoadedFor] =
        React.useState("");
    const [availabilityDataError, setAvailabilityDataError] = React.useState(false);
    const [slotsAvailableForSelectedDay, setSlotsAvailableForSelectedDay] =
        React.useState<string[]>();
    const noDefinedOfficeWindows =
        selectedDay &&
        officeWindows.length === 0 &&
        draftRequestOfficeWindow.location !== "Remote";
    const availabilityDataReady =
        !availabilityDataLoading &&
        availabilityDataLoadedFor === draftRequestOfficeWindow.location;
    const hasClinicianProfile = Boolean(loggedInClinicianData?.email);

    React.useEffect(() => {
        let cancelled = false;
        const location = draftRequestOfficeWindow.location;

        if (!requestOfficeDetails.show) {
            return () => {
                cancelled = true;
            };
        }

        setFormattedWindows(undefined);
        setAvailabilityDataLoadedFor("");
        setAvailabilityDataError(false);
        setOfficeWindows([]);
        setHeldWindows([]);
        setClinicianList([]);

        if (!location || location === "Remote") {
            setAvailabilityDataLoading(false);
            setAvailabilityDataLoadedFor(location);
            return () => {
                cancelled = true;
            };
        }

        setAvailabilityDataLoading(true);
        Promise.all([
            store.getUnPaginatedOfficeWndows(location),
            store.getAllHeldOfficeWindowsByLocation(location),
            store.fetchAllCliniciansByLocation(location),
        ])
            .then(([availableWindows, heldRequests, clinicians]) => {
                if (cancelled) return;

                setOfficeWindows(availableWindows || []);
                setHeldWindows(
                    heldRequests.map((item) => ({
                        clinicianId: item.clinician_name,
                        start: item.start_date.toISOString(),
                        end: item.end_date.toISOString(),
                        location: item.location,
                        office: item.office,
                    }))
                );
                setClinicianList(clinicians || []);
                setAvailabilityDataLoadedFor(location);
            })
            .catch((error) => {
                if (cancelled) return;
                setAvailabilityDataError(true);
                console.error("Unable to load office-window availability:", error);
                addToast(
                    "Unable to load office-window availability. Please retry.",
                    {
                        appearance: "error",
                        autoDismiss: false,
                    }
                );
            })
            .finally(() => {
                if (!cancelled) setAvailabilityDataLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [addToast, draftRequestOfficeWindow.location, requestOfficeDetails.show]);

    let maxApptDatePossible = moment().add(MAX_APPT_DATE_DIFF_WEEKS, "weeks");

    const existingSchedules = React.useMemo(() => {
        let schedules: IOfficeWindowSchedule[] = [];
        clinicianList.forEach((c) => {
            schedules = schedules.concat(
                (c.office_windows || [])
                    .filter((w) => w.location === draftRequestOfficeWindow?.location)
                    .map((w) => {
                        return {
                            clinicianId: c.email,
                            id: w.id,
                            assigned_to: `${c.firstname} ${c.lastname}`,
                            start: w.start,
                            end: w.end,
                            ending_date: w.ending_date,
                            office: w.office,
                            location: draftRequestOfficeWindow?.location,
                        };
                    })
            );
        });
        return [...schedules, ...heldWindows];
    }, [clinicianList, draftRequestOfficeWindow?.location, heldWindows]);

    React.useEffect(() => {
        const [fromTimeHr, fromTimeMin] = selectedFromTime.split(":");
        const [toTimeHr, toTimeMin] = selectedToTime.split(":");
        const fromDate = startingDate
            .clone()
            .day(selectedDay)
            .hour(parseInt(fromTimeHr))
            .minute(parseInt(fromTimeMin))
            .second(0)
            .milliseconds(0);
        const toDate = startingDate
            .clone()
            .day(selectedDay)
            .hour(
                selectedEndTime || selectedTimeFromSuggestion
                    ? parseInt(toTimeHr)
                    : parseInt(fromTimeHr) + 4
            )
            .minute(
                selectedEndTime || selectedTimeFromSuggestion
                    ? parseInt(toTimeMin)
                    : parseInt(fromTimeMin)
            )
            .second(0)
            .milliseconds(0);

        const createRequestEndDate = startingDate
            .clone()
            .day(selectedDay)
            .hour(selectedEndTime ? parseInt(toTimeHr) : parseInt(fromTimeHr) + 1)
            .minute(selectedEndTime ? parseInt(toTimeMin) : parseInt(fromTimeMin))
            .second(0)
            .milliseconds(0);

        let updatedEndingDate = endingDate
            ? endingDate.clone().hour(toDate.hour()).minute(toDate.minute())
            : toDate;

        const hasConflict = hasConflictingAvailability(
            existingSchedules,
            fromDate.toISOString(),
            viewAs === "office-manager"
                ? createRequestEndDate.toISOString()
                : updatedEndingDate.toISOString(),
            draftRequestOfficeWindow.office,
            draftRequestOfficeWindow.location,
            loggedInUserEmail as string
        );
        setHasConflict(hasConflict as IOfficeWindowSchedule);

        if (loggedInClinicianData) {
            const loggedInClinicianWindows = loggedInClinicianData?.office_windows;
            // function to check that clinician do not request for windows that clash with their existing window
            const getConflictsOnRequestForClinician = async () => {
                const requestHasConflict =
                    await hasConflictingAvailabilityWithCliniciansRemoteSchedules(
                        loggedInClinicianWindows || ({} as IOfficeWindow[]),
                        fromDate.toISOString(),
                        viewAs === "office-manager"
                            ? createRequestEndDate.toISOString()
                            : updatedEndingDate.toISOString()
                    );
                setRequestHasConflictWithCliniciansSchedule(requestHasConflict);
            };
            selectedDay !== "" && getConflictsOnRequestForClinician();
        }
        if (officeWindows.length > 0 && selectedDay !== "") {
            const isValidOfficeWindow = isWithinOfficeWindow(
                officeWindows,
                fromDate.toISOString(),
                updatedEndingDate.toISOString(),
                draftRequestOfficeWindow.office,
                draftRequestOfficeWindow.location
            );
            setIsValidOfficeWindow(isValidOfficeWindow);
        }

        if (
            viewAs === "office-manager" &&
            draftRequestOfficeWindow.location !== "" &&
            draftRequestOfficeWindow.office !== "" &&
            draftRequestOfficeWindow.day !== ""
        ) {
            // get all the times that are not available into one array
            const getAllUnAvailableTimesForSelectedDay = () => {
                const timesUsedByOtherClinicians = getTimesUsedByOtherClincians(
                    existingSchedules,
                    draftRequestOfficeWindow.location,
                    draftRequestOfficeWindow.office,
                    draftRequestOfficeWindow.day
                );
                const timesUsedByOfficeManagers = getTimesUsedByOtherClincians(
                    officeWindows,
                    draftRequestOfficeWindow.location,
                    draftRequestOfficeWindow.office,
                    draftRequestOfficeWindow.day
                );
                const allUnAvailableTimes = [
                    ...timesUsedByOtherClinicians,
                    ...timesUsedByOfficeManagers,
                ];
                return allUnAvailableTimes;
            };
            // this is the time slots that are occupied for that day
            const occupiedTimeSlots = getAllUnAvailableTimesForSelectedDay();

            const slotsAvailable =
                findAvailableTimeSlotsClinicianCanBook(occupiedTimeSlots);
            setSlotsAvailableForSelectedDay(slotsAvailable);
        }

        if (officeWindows.length > 0 && viewAs === "office-manager") {
            const hasConflictWithAlreadyCreatedOfficeWindows =
                hasConflictingAvailability(
                    officeWindows,
                    fromDate.toISOString(),
                    createRequestEndDate.toISOString(),
                    draftRequestOfficeWindow.office,
                    draftRequestOfficeWindow.location,
                    loggedInUserEmail as string
                );
            setNewOfficeWindowClash(hasConflictWithAlreadyCreatedOfficeWindows);
        }
        const formattedDate = handleFormatDatePickerValue(
            endingDate?.toDate() as unknown as string
        );

        const newEndingDate = moment(`${formattedDate}T00:00:00.000`)
            .hour(toDate.hour())
            .minute(toDate.minute())
            .toDate();

        setDraftRequestOfficeWindow({
            ...draftRequestOfficeWindow,
            start_date: fromDate.toDate(),
            end_date: toDate.toDate(),
            ending_date: selectedEndDate ? newEndingDate : "",
            assigned_to: (hasConflict?.assigned_to as string) || "",
            clinician_name:
                loggedInClinicianData?.firstname +
                " " +
                loggedInClinicianData?.lastname,
            office:
                draftRequestOfficeWindow.location === "Remote"
                    ? `${loggedInClinicianData?.firstname} ${loggedInClinicianData?.lastname}`
                    : draftRequestOfficeWindow.office,
            delivery_option:
                draftRequestOfficeWindow.location === "Remote"
                    ? "remote"
                    : "in_person",
        });

        setDraftCreateOfficeWindow({
            ...draftCreateOfficeWindow,
            location: draftRequestOfficeWindow.location,
            office: draftRequestOfficeWindow.office,
            start: fromDate.toISOString(),
            end: createRequestEndDate.toISOString(),
        });

        const getLocations = async () => {
            if (viewAs === "office-manager") {
                const locations = await getOfficeManagerLocations();
                setOfficeManagerLocations(locations);
            }
        };
        getLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        draftRequestOfficeWindow?.office,
        draftRequestOfficeWindow?.location,
        draftCreateOfficeWindow.location,
        draftCreateOfficeWindow?.office,
        selectedFromTime,
        selectedEndTime,
        endingDate,
        startingDate,
        selectedDay,
        selectedToTime,
        refreshKey,
        existingSchedules,
        heldWindows,
        officeWindows,
        loggedInClinicianData,
    ]);

    React.useEffect(() => {
        const location = draftRequestOfficeWindow.location;
        const day = draftRequestOfficeWindow.day;
        const availabilityDataReady =
            !availabilityDataLoading && availabilityDataLoadedFor === location;

        if (
            viewAs !== "clinician" ||
            !location ||
            location === "Remote" ||
            !day ||
            !loggedInClinicianData ||
            !availabilityDataReady
        ) {
            setFormattedWindows(undefined);
            return;
        }

        const [fromTimeHr, fromTimeMin] = selectedFromTime.split(":");
        const fromDate = startingDate
            .clone()
            .day(selectedDay)
            .hour(parseInt(fromTimeHr))
            .minute(parseInt(fromTimeMin))
            .second(0)
            .milliseconds(0);
        const officesAndTimesAvailable = suggestAvailableOfficesAndTimes({
            availableOffices,
            location,
            day,
            loggedInClinicianWindows: loggedInClinicianData.office_windows || [],
            existingSchedules,
            heldWindows,
            officeWindows,
            fromDate,
        });

        const handleClickTimeSlot = (
            officeNumber: string,
            suggestedFromTime: string,
            suggestedToTime: string
        ) => {
            setDraftRequestOfficeWindow((current) => ({
                ...current,
                office: officeNumber,
            }));
            setSelectedFromTime(
                moment(suggestedFromTime, ["h:mm A"]).format("HH:mm")
            );
            setSelectedToTime(moment(suggestedToTime, ["h:mm A"]).format("HH:mm"));
            setSelectedTimeFromSuggestion(true);
            setSelectedEndTime(false);
        };

        setFormattedWindows(
            generateSuggestedWindows(
                officesAndTimesAvailable.availableOfficesAndTimes,
                handleClickTimeSlot
            )
        );
    }, [
        availabilityDataLoadedFor,
        availabilityDataLoading,
        availableOffices,
        draftRequestOfficeWindow.day,
        draftRequestOfficeWindow.location,
        existingSchedules,
        heldWindows,
        loggedInClinicianData,
        officeWindows,
        selectedDay,
        selectedFromTime,
        startingDate,
        viewAs,
    ]);

    const formattedClinicianDate = moment(draftRequestOfficeWindow.end_date).format(
        DateFormats.HOUR_MIN_FORMAT
    );

    const handleEndDateChange = (selectedDate: Date | null) => {
        if (selectedDate) {
            setSelectedEndDate(true);
            setEndingDate(moment(selectedDate));
        } else {
            setEndingDate(null);
        }
    };

    const handleClinicianEndTimeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSelectedTimeFromSuggestion(false);
        setSelectedEndTime(true);
        setSelectedToTime(e.target.value);
        const selectedTime = moment(e.target.value, "hh:mm A").format("HH:mm");

        // Combine the current end date with the selected end time
        const newEndingDateTime =
            moment(draftRequestOfficeWindow.end_date).format("YYYY-MM-DD") +
            " " +
            selectedTime;
        const newEndingDate = moment(newEndingDateTime, "YYYY-MM-DD HH:mm");
        setEndingDate(newEndingDate);
    };

    const handleOfficeManagerEndTimeChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSelectedEndTime(true);
        setSelectedToTime(e.target.value);
        const selectedTime = moment(e.target.value, "hh:mm A").format("HH:mm");

        // Combine the current end date with the selected end time
        const newEndingDateTime =
            moment(draftCreateOfficeWindow.end).format("YYYY-MM-DD") +
            " " +
            selectedTime;
        const newEndingDate = moment(newEndingDateTime, "YYYY-MM-DD HH:mm");
        setEndingDate(newEndingDate);
    };

    const handleDaySelect = (val: Option) => {
        setFormattedWindows(undefined);
        setSelectedDay(val.value);
        let numberOfWeeks = getNumberOfWeeks(draftRequestOfficeWindow.recurrence);
        const nextSelectedDay = getNextXWeeksMoment(
            moment(draftCreateOfficeWindow.start),
            numberOfWeeks,
            selectedDay
        );
        setStartingDate(nextSelectedDay);

        setDraftRequestOfficeWindow({
            ...draftRequestOfficeWindow,
            day: val.value,
        });
    };

    React.useEffect(() => {
        const startingDates = getAvailableRequestStartDates(
            draftRequestOfficeWindow
        );
        setAvailableApptStartingDates(startingDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        draftRequestOfficeWindow.end_date,
        draftRequestOfficeWindow.start_date,
        selectedDay,
    ]);

    const showClinicianWindowConflictToast = (conflictingWindow: IOfficeWindow) => {
        addToast(
            [
                "The time range selected conflicts with your office window for",
                `${conflictingWindow.location}, ${conflictingWindow.office}`,
                `@ ${moment(
                    getStringFromScheduleDate(conflictingWindow.start)
                ).format(DateFormats.DAY_HOUR_MIN_FORMAT)} - ${moment(
                    getStringFromScheduleDate(conflictingWindow.end)
                ).format(DateFormats.DAY_HOUR_MIN_FORMAT)}`,
            ].join(" "),
            { appearance: "error", autoDismiss: false }
        );
    };

    const handleRequestOfficeWindow = async () => {
        let latestClinicianData: IClinician | undefined;
        try {
            latestClinicianData = await store.getClinicianByEmail(
                loggedInUserEmail as string
            );
        } catch {
            addToast("Unable to verify your schedule. Please try again.", {
                appearance: "error",
                autoDismiss: false,
            });
            return;
        }
        if (!latestClinicianData) {
            addToast("Unable to load your clinician record. Please try again.", {
                appearance: "error",
                autoDismiss: false,
            });
            return;
        }
        const latestConflict =
            await hasConflictingAvailabilityWithCliniciansRemoteSchedules(
                latestClinicianData.office_windows || [],
                getStringFromScheduleDate(draftRequestOfficeWindow.start_date),
                getStringFromScheduleDate(draftRequestOfficeWindow.end_date)
            );
        if (latestConflict) {
            setRequestHasConflictWithCliniciansSchedule(latestConflict);
            showClinicianWindowConflictToast(latestConflict);
            return;
        }

        if (draftRequestOfficeWindow.location === "Remote") {
            const bookingData = {
                id: uuidv4(),
                clinicianId: loggedInUserEmail as string,
                office: draftRequestOfficeWindow.office,
                location: draftRequestOfficeWindow.location,
                start: getStringFromScheduleDate(
                    draftRequestOfficeWindow.start_date
                ),
                end: getStringFromScheduleDate(draftRequestOfficeWindow.end_date),
                ending_date: getStringFromScheduleDate(
                    draftRequestOfficeWindow.ending_date
                ),
            };
            await store.addClinicianOfficeWindow(bookingData);

            addToast("Office window created successfully", {
                appearance: "success",
            });
        }
        if (draftRequestOfficeWindow.location !== "Remote") {
            const customUUID = uuidv4();
            const success = await store.createClinicianOfficeWindowRequests({
                clinician_request_data: {
                    ...draftRequestOfficeWindow,
                    category: "request_list",
                    waitlist_count: 0,
                    request_list_count: 1,
                    office_window_request_id: customUUID,
                },
                intakeSignature,
            });

            if (!success) {
                addToast("Failed to create office window request", {
                    appearance: "error",
                });
            } else {
                addToast("Office window request created successfully", {
                    appearance: "success",
                });
            }
        }
        setRequestOfficeDetails({
            ...requestOfficeDetails,
            show: false,
        });
        setDraftRequestOfficeWindow({
            ...draftRequestOfficeWindow,
            location: "",
            office: "",
            recurrence: RecurrenceRule.WEEKLY,
            start_date: new Date(),
            end_date: new Date(),
            ending_date: "",
            change_end_time_reason: "",
            comment: "",
            request_date: new Date(),
            day: "",
            requester: loggedInUserEmail as string,
        });
        setSelectedEndTime(false);
    };

    const handleJoinWaitList = async () => {
        const customUUID = uuidv4();

        const success = await store.createClinicianOfficeWindowRequests({
            clinician_request_data: {
                ...draftRequestOfficeWindow,
                category: "wait_list",
                waitlist_count: 1,
                request_list_count: 0,
                office_window_request_id: customUUID,
            },
            intakeSignature,
        });

        if (!success) {
            addToast("Failed to add to waitlist", {
                appearance: "error",
            });
        } else {
            addToast("Office request added to waitlist successfully", {
                appearance: "success",
            });
        }
        setRequestOfficeDetails({
            ...requestOfficeDetails,
            show: false,
        });
        setDraftRequestOfficeWindow({
            ...draftRequestOfficeWindow,
            location: "",
            office: "",
            recurrence: RecurrenceRule.WEEKLY,
            start_date: new Date(),
            end_date: new Date(),
            change_end_time_reason: "",
            comment: "",
            ending_date: "",
            request_date: new Date(),
            day: "",
            requester: loggedInUserEmail as string,
        });
        setSelectedEndTime(false);
    };

    // Calculate the time difference in milliseconds
    const timeDifference =
        draftRequestOfficeWindow.end_date.getTime() -
        draftRequestOfficeWindow.start_date.getTime();

    // Check if the time difference is 4 hours (in milliseconds)
    const isFourHoursApart = Math.abs(timeDifference) === 4 * 60 * 60 * 1000;

    const handleCreateOfficeWindow = async () => {
        const customUUID = uuidv4();
        const isValidEndDate = isValidISODate(draftCreateOfficeWindow.end);
        const isValidStartDate = isValidISODate(draftCreateOfficeWindow.start);

        if (hasConflict) {
            addToast(
                [
                    `
  Conflicting schedule with ${hasConflict.clinicianId},`,
                    `${hasConflict.location}, ${hasConflict.office}`,
                    `@ ${moment(getStringFromScheduleDate(hasConflict.start)).format(
                        DateFormats.DAY_HOUR_FORMAT
                    )} - ${moment(getStringFromScheduleDate(hasConflict.end)).format(
                        DateFormats.DAY_HOUR_FORMAT
                    )}`,
                ].join(" "),
                { appearance: "error", autoDismiss: false }
            );
            return;
        }
        if (newOfficeWindowClash) {
            addToast(
                [
                    `
  Conflicting schedule with office window set for`,
                    `${newOfficeWindowClash.location}, ${newOfficeWindowClash.office}`,
                    `@ ${moment(
                        getStringFromScheduleDate(newOfficeWindowClash.start)
                    ).format(DateFormats.DAY_HOUR_FORMAT)} - ${moment(
                        getStringFromScheduleDate(newOfficeWindowClash.end)
                    ).format(DateFormats.DAY_HOUR_FORMAT)}`,
                ].join(" "),
                { appearance: "error", autoDismiss: false }
            );
            return;
        }
        if (
            !hasConflict &&
            !newOfficeWindowClash &&
            isValidStartDate &&
            isValidEndDate
        ) {
            const success = await store.createAvailableOfficeWindows({
                office_window_data: {
                    ...draftCreateOfficeWindow,
                    id: customUUID,
                },
                loggedInUserEmail: loggedInUserEmail as string,
                type: "created_by_office_manager",
            });
            if (!success) {
                addToast("Failed to create office window", {
                    appearance: "error",
                });
            } else {
                addToast("Office window created successfully", {
                    appearance: "success",
                });
                setRequestOfficeDetails({
                    ...requestOfficeDetails,
                    show: false,
                });
                setRefreshKey((prevKey) => prevKey + 1);
                setDraftCreateOfficeWindow({
                    ...draftCreateOfficeWindow,
                    location: "",
                    office: "",
                });
                setDraftRequestOfficeWindow({
                    ...draftRequestOfficeWindow,
                    location: "",
                    office: "",
                    day: "",
                });
                setSelectedFromTime("07:00");
                setSelectedToTime("08:00");
            }
        }
    };

    const isRequiredFieldProvided = (data: any) => {
        let isValid = true;

        if (
            data.location === "" ||
            data.day === "" ||
            selectedDay === "" ||
            data.delivery_option === "" ||
            data.office === ""
        ) {
            isValid = false;
        }

        if (!isFourHoursApart && draftRequestOfficeWindow.location !== "Remote") {
            if (data.change_end_time_reason === "") {
                isValid = false;
            }
        }
        if (requestHasConflictWithCliniciansSchedule) {
            isValid = false;
        }
        if (viewAs === "clinician" && !hasClinicianProfile) {
            isValid = false;
        }
        if (
            (viewAs === "clinician" && clinicianProfileLoading) ||
            availabilityDataLoading ||
            availabilityDataError ||
            !availabilityDataReady
        ) {
            isValid = false;
        }
        if (
            moment(draftRequestOfficeWindow.end_date).isBefore(
                moment(draftRequestOfficeWindow.start_date)
            )
        ) {
            isValid = false;
        }

        return isValid;
    };

    // filteredOffices for logged in OfficeManagers
    const filteredOffices = Object.entries(availableOffices)
        .filter(
            ([location]) =>
                officeManagerLocations && officeManagerLocations.includes(location)
        )
        .reduce((acc, [location, offices]) => {
            acc[location] = offices;
            return acc;
        }, {});

    const excludedOffices = new Set(["Brooklyn DUMBO", "Columbus Circle"]);

    const getLocationOptions = (): Option[] => {
        if (viewAs === "office-manager") {
            return (
                officeManagerLocations?.map((location) => ({
                    label: location,
                    value: location,
                })) || []
            );
        } else {
            return Object.keys(availableOffices)
                .filter((location) => !excludedOffices.has(location))
                .map((location) => ({
                    label: location,
                    value: location,
                }));
        }
    };

    const getDayOptions = [
        {
            label: "Select a day",
            value: "",
        },
        ...Object.values(DayOption).map((s) => ({
            label: s,
            value: s,
        })),
    ];

    const getAvailableOfficeOptions = () => {
        return sortStringsWithNumberEndings(
            uniq(availableOffices[draftRequestOfficeWindow.location] || [])
        ).map((r) => ({
            label: r,
            value: r,
        }));
    };

    const getFilteredOptions = () => {
        return sortStringsWithNumberEndings(
            uniq(filteredOffices[draftRequestOfficeWindow.location])
        ).map((r) => ({
            label: r,
            value: r,
        }));
    };

    const getDeliveryOptions = () => {
        return [
            { label: "Select delivery option", value: "" },
            { label: "In-Person", value: "in_person" },
            { label: "Remote", value: "remote" },
        ];
    };

    const getDialogProps = () => {
        if (viewAs === "office-manager") {
            return {
                saveText: "Confirm",
                cancelText: "Cancel",
                onSave: handleCreateOfficeWindow,
                onCancel: () =>
                    setRequestOfficeDetails({
                        ...requestOfficeDetails,
                        show: false,
                    }),
            };
        } else {
            // For clinicians, determine if they should join waitlist or request
            const shouldJoinWaitlist =
                draftRequestOfficeWindow.location !== "Remote" &&
                draftRequestOfficeWindow.office !== "" &&
                ((Boolean(hasConflict) &&
                    !requestHasConflictWithCliniciansSchedule) ||
                    (isValidOfficeWindow === false &&
                        !requestHasConflictWithCliniciansSchedule) ||
                    noDefinedOfficeWindows);

            return {
                saveText: shouldJoinWaitlist ? "Join Waitlist" : "Request",
                cancelText: "Cancel",
                onSave: shouldJoinWaitlist
                    ? handleJoinWaitList
                    : handleRequestOfficeWindow,
                onCancel: () =>
                    setRequestOfficeDetails({
                        ...requestOfficeDetails,
                        show: false,
                    }),
            };
        }
    };

    const dialogProps = getDialogProps();

    return (
        <Dialog
            open={requestOfficeDetails.show}
            onClose={requestOfficeDetails.onHide ?? (() => {})}
            title={
                viewAs === "office-manager"
                    ? "Add New Office Availability"
                    : "Request Office Window"
            }
            saveText={dialogProps.saveText}
            cancelText={dialogProps.cancelText}
            onSave={dialogProps.onSave}
            onCancel={dialogProps.onCancel}
            isSubmitBtnDisabled={!isRequiredFieldProvided(draftRequestOfficeWindow)}
        >
            <form className="v2-form">
                {viewAs === "clinician" && (
                    <Alert type="info">Note: All windows are weekly windows</Alert>
                )}
                <Select
                    isRequired
                    options={getLocationOptions()}
                    placeholder="Select location"
                    value={
                        getLocationOptions().find(
                            (option) =>
                                option.value === draftRequestOfficeWindow.location
                        ) || { label: "", value: "" }
                    }
                    label="Location"
                    onChange={(val) => {
                        setFormattedWindows(undefined);
                        setDraftRequestOfficeWindow({
                            ...draftRequestOfficeWindow,
                            location: (val as Option)?.value,
                            office: "",
                        });
                    }}
                    isLongListInDialog
                />
                {viewAs === "clinician" && (
                    <>
                        <Select
                            isRequired
                            label="Day"
                            options={getDayOptions}
                            value={
                                getDayOptions.find(
                                    (option) =>
                                        option.value === draftRequestOfficeWindow.day
                                ) || { label: "", value: "" }
                            }
                            onChange={(val) => handleDaySelect(val as Option)}
                            isLongListInDialog
                        />

                        {draftRequestOfficeWindow.location !== "" &&
                            draftRequestOfficeWindow.location !== "Remote" &&
                            selectedDay !== "" && (
                                <>
                                    {availabilityDataError ? (
                                        <Alert type="info">
                                            Available office windows could not be
                                            loaded. Please close this form and try
                                            again.
                                        </Alert>
                                    ) : clinicianProfileLoading ||
                                      availabilityDataLoading ||
                                      !availabilityDataReady ? (
                                        <Alert type="info">
                                            Checking available office windows…
                                        </Alert>
                                    ) : !hasClinicianProfile ? (
                                        <Alert type="info">
                                            A clinician profile is required to
                                            request an office window. Please use an
                                            account linked to a clinician record.
                                        </Alert>
                                    ) : !formattedWindows ||
                                      formattedWindows.length === 0 ? (
                                        <Alert type="info">
                                            There is currently no available window
                                            for {draftRequestOfficeWindow.location}{" "}
                                            on {selectedDay}. If you want to proceed
                                            anyway, select an office/time range and
                                            join the waitlist.
                                        </Alert>
                                    ) : (
                                        <div>
                                            <Alert type="info">
                                                NB: The suggestions below are the
                                                only available windows for{" "}
                                                {draftRequestOfficeWindow.location}{" "}
                                                on {selectedDay}, You can click on
                                                one of these suggestions to use
                                                Alternatively, if you want a window
                                                outside these suggestions you can
                                                select an office and time range to
                                                join the waitlist for{" "}
                                                {draftRequestOfficeWindow.location}{" "}
                                                on {selectedDay}
                                            </Alert>

                                            {formattedWindows}
                                        </div>
                                    )}
                                </>
                            )}
                        <Select
                            label="Office No"
                            options={getAvailableOfficeOptions()}
                            isRequired
                            onChange={(val) => {
                                setDraftRequestOfficeWindow({
                                    ...draftRequestOfficeWindow,
                                    office: (val as Option)?.value,
                                });
                                setSelectedTimeFromSuggestion(false);
                                setUserSelectedOffice(true);
                            }}
                            value={
                                getAvailableOfficeOptions().find(
                                    (option) =>
                                        option.value ===
                                        draftRequestOfficeWindow.office
                                ) || { label: "", value: "" }
                            }
                            isLongListInDialog
                        />
                    </>
                )}
                {viewAs === "office-manager" && (
                    <div className="v2-form">
                        <Select
                            label="Office No"
                            options={getFilteredOptions()}
                            isRequired
                            onChange={(val) => {
                                setDraftRequestOfficeWindow({
                                    ...draftRequestOfficeWindow,
                                    office: (val as Option)?.value,
                                });
                                setSelectedTimeFromSuggestion(false);
                                setUserSelectedOffice(true);
                            }}
                            value={
                                getFilteredOptions().find(
                                    (option) =>
                                        option.value ===
                                        draftRequestOfficeWindow.office
                                ) || { label: "", value: "" }
                            }
                            isLongListInDialog
                        />
                        <Select
                            isRequired
                            label="Day"
                            options={getDayOptions}
                            value={
                                getDayOptions.find(
                                    (option) =>
                                        option.value === draftRequestOfficeWindow.day
                                ) || { label: "", value: "" }
                            }
                            onChange={(val) => handleDaySelect(val as Option)}
                            isLongListInDialog
                        />
                    </div>
                )}

                {viewAs === "office-manager" &&
                    slotsAvailableForSelectedDay &&
                    slotsAvailableForSelectedDay?.length > 0 && (
                        <Alert type="info">
                            Available time for {selectedDay} at{" "}
                            {draftRequestOfficeWindow.location} you can set as other
                            times are occupied includes
                            <strong>
                                {" "}
                                {slotsAvailableForSelectedDay.join(", ")}
                            </strong>
                        </Alert>
                    )}

                {viewAs === "office-manager" ? (
                    <div className="v2-form gridTwo">
                        <Input
                            label="Starting"
                            type="time"
                            min="07:00"
                            max="22:00"
                            step={900}
                            isRequired
                            value={selectedFromTime}
                            onChange={(e) => {
                                setSelectedFromTime(e.target.value);
                            }}
                        />
                        <Input
                            label="To"
                            type="time"
                            min="07:00"
                            max="22:00"
                            step={900}
                            isRequired
                            value={selectedToTime}
                            onChange={handleOfficeManagerEndTimeChange}
                        />
                    </div>
                ) : (
                    <div className="v2-form">
                        <div className="gridTwo">
                            <Input
                                label="Starting"
                                type="time"
                                min="07:00"
                                step={900}
                                isRequired
                                value={selectedFromTime}
                                onChange={(e) => {
                                    setSelectedFromTime(e.target.value);
                                    setSelectedTimeFromSuggestion(false);
                                    setUserSelectedFromTime(true);
                                }}
                            />
                            <Input
                                label="To"
                                min="07:00"
                                type="time"
                                step={900}
                                isRequired
                                value={
                                    selectedEndTime || selectedTimeFromSuggestion
                                        ? selectedToTime
                                        : moment(
                                              formattedClinicianDate,
                                              "hh:mm A"
                                          ).format("HH:mm")
                                }
                                onChange={handleClinicianEndTimeChange}
                            />
                        </div>
                        {!isFourHoursApart ? null : (
                            <TextArea
                                label="Enter any comments you have about the request"
                                value={draftRequestOfficeWindow.comment}
                                onChange={(e) => {
                                    setDraftRequestOfficeWindow({
                                        ...draftRequestOfficeWindow,
                                        comment: e.target.value,
                                    });
                                }}
                            />
                        )}
                        {!isFourHoursApart &&
                            draftRequestOfficeWindow.location !== "Remote" && (
                                <TextArea
                                    label="Provide a
                                    reason why the request is not a 4-hour window.
                                    "
                                    isRequired
                                    value={
                                        draftRequestOfficeWindow.change_end_time_reason
                                    }
                                    onChange={(e) => {
                                        setDraftRequestOfficeWindow({
                                            ...draftRequestOfficeWindow,
                                            change_end_time_reason: e.target.value,
                                        });
                                    }}
                                    className={
                                        draftRequestOfficeWindow.change_end_time_reason
                                            ? ""
                                            : "is-invalid"
                                    }
                                />
                            )}
                        {Boolean(requestHasConflictWithCliniciansSchedule) &&
                            (draftRequestOfficeWindow.location === "Remote" ||
                                selectedTimeFromSuggestion ||
                                userSelectedFromTime ||
                                userSelectedOffice ||
                                selectedEndTime) && (
                                <Alert type="info">
                                    The time range selected conflicts with your
                                    office window on {selectedDay} from{" "}
                                    {moment(
                                        requestHasConflictWithCliniciansSchedule?.start
                                    ).format(DateFormats.HOUR_MIN_FORMAT)}{" "}
                                    -{" "}
                                    {moment(
                                        requestHasConflictWithCliniciansSchedule?.end
                                    ).format(DateFormats.HOUR_MIN_FORMAT)}
                                    , you cannot create this request, Kindly check
                                    your office windows for {selectedDay} to confirm.
                                </Alert>
                            )}
                        {viewAs === "clinician" && (
                            <Select
                                label="Delivery Option"
                                isRequired
                                options={getDeliveryOptions()}
                                value={
                                    getDeliveryOptions().find(
                                        (option) =>
                                            option.value ===
                                            draftRequestOfficeWindow.delivery_option
                                    ) || { label: "", value: "" }
                                }
                                onChange={(val) => {
                                    setDraftRequestOfficeWindow({
                                        ...draftRequestOfficeWindow,
                                        delivery_option: (val as Option)?.value,
                                    });
                                }}
                            />
                        )}
                        <div className="gridTwo">
                            <DatePicker
                                label="Start Date"
                                dateFormat={"MM/dd/yyyy"}
                                isRequired
                                selected={
                                    selectedStartDate
                                        ? startingDate.toDate()
                                        : draftRequestOfficeWindow.start_date
                                }
                                minDate={draftRequestOfficeWindow.start_date}
                                includeDates={availableApptStartingDates.map((a) =>
                                    a.toDate()
                                )}
                                maxDate={maxApptDatePossible.toDate()}
                                onChange={(date) => {
                                    setSelectedStartDate(true);
                                    setStartingDate(moment(date as Date));
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                dateFormat={"MM/dd/yyyy"}
                                selected={
                                    selectedEndDate ? endingDate?.toDate() : null
                                }
                                minDate={new Date()}
                                onChange={handleEndDateChange}
                            />
                        </div>
                    </div>
                )}
            </form>
        </Dialog>
    );
}
